import { useState, useEffect, useCallback } from 'react'

import useGetRefundsQuery from '@src/hooks/queries/refund/useGetRefundsQuery'
import useUpdatePaybackStatusMutate from '@src/hooks/queries/refund/useUpdatePaybackStatusMutate'
import useProcessRefundMutate from '@src/hooks/queries/refund/useProcessRefundMutate'
import type { Refund } from '@src/type/refund'

const useRefundList = ({ itemPerPage = 10, currentPage = 1 }) => {
	const [refunds, setRefunds] = useState<Refund[]>([])
	const [total, setTotal] = useState(0)

	const offset = currentPage - 1 > 0 ? (currentPage - 1) * itemPerPage : 0

	const payload = {
		getTotal: true,
		limit: itemPerPage,
		offset,
	}

	const {
		data: refundsData,
		refetch, // INFO: refetch 메소드는 캐시 설정을 하더라도 캐시를 사용하지 않는다.,
		isFetching,
		isRefetching,
	} = useGetRefundsQuery(payload, {
		enabled: refunds.length === 0,
		retry: false,
		cacheTime: 0,
		keepPreviousData: true,
	})

	const { mutate: updatePaybackStatus, isLoading: isRejecting } =
		useUpdatePaybackStatusMutate(offset)

	const { mutate: processRefund, isLoading: isConfirming } = useProcessRefundMutate(offset)

	useEffect(() => {
		if (!refundsData || !refundsData?.data.product_challenge_payback || !refundsData?.data.count) {
			return
		}

		const {
			data: { product_challenge_payback: refundList, count: totalRefunds },
		} = refundsData

		setRefunds(refundList)
		setTotal(totalRefunds)
	}, [refundsData])

	useEffect(() => {
		if (currentPage <= 0) {
			return
		}

		refetch().catch((e) => console.log(e))
	}, [currentPage, refetch])

	const rejectPayback = useCallback(
		({
			paybackNo,
			reason,
			onSuccess,
			onError,
		}: {
			paybackNo: string
			reason: string
			onSuccess?: () => void
			onError?: () => void
		}) => {
			updatePaybackStatus(
				{ paybackNo, status: 'declined', reason },
				{
					onSuccess,
					onError,
				},
			)
		},
		[updatePaybackStatus],
	)

	const confirmPayback = useCallback(
		({
			paybackNo,
			amount,
			onSuccess,
			onError,
		}: {
			paybackNo: string
			amount?: number
			onSuccess?: () => void
			onError?: () => void
		}) => {
			processRefund(
				{
					paybackNo,
					amount,
				},
				{
					onSuccess,
					onError,
				},
			)
		},
		[processRefund],
	)

	return {
		refetch,
		refunds,
		total,
		isFetching,
		isRefetching,
		rejectPayback,
		isRejecting,
		confirmPayback,
		isConfirming,
	}
}

export default useRefundList
